import { Link, useForm, usePage } from '@inertiajs/react';
import type { FormEvent } from 'react';

import Icon from '@/components/public/ui/Icon';
import { inputClass } from '@/lib/formStyles';
import { parishName, parishLogo, useSite } from '@/hooks/useSite';

type FlashProps = { flash: { notice?: string; alert?: string } };

function loginAlert(message: string) {
  const title = message.includes('expirou')
    ? 'Sessão expirada'
    : message.includes('iniciar sessão')
      ? 'Acesso reservado'
      : 'Não foi possível iniciar sessão';

  return (
    <div
      role="alert"
      className="border-error/25 bg-error/[0.06] text-on-surface flex gap-3 rounded-xl border p-4 shadow-sm"
    >
      <span className="bg-error/10 text-error flex h-9 w-9 shrink-0 items-center justify-center rounded-full">
        <Icon name="error" size="sm" />
      </span>
      <span className="min-w-0">
        <span className="font-headline block text-sm font-semibold">{title}</span>
        <span className="text-on-surface-light mt-1 block text-sm leading-relaxed">{message}</span>
      </span>
    </div>
  );
}

export default function Login() {
  const { flash } = usePage<FlashProps>().props;
  const site = useSite();
  const form = useForm({ email: '', password: '' });
  const { data, setData, processing, errors } = form;

  const submit = (e: FormEvent) => {
    e.preventDefault();
    form.transform((d) => ({ user: d }));
    form.post('/users/sign_in');
  };

  // Devise redirects failed sign-ins with a flash alert; field errors are rare.
  const authError = flash?.alert ?? [errors.email].flat()[0];

  return (
    <main className="bg-background flex min-h-screen items-center justify-center p-4">
      <div className="border-line bg-surface grid w-full max-w-3xl overflow-hidden rounded-lg border shadow-xl md:grid-cols-2">
        {/* Brand panel */}
        <div className="section-ink azulejo-on-ink relative hidden flex-col justify-center p-9 md:flex">
          <img
            src={parishLogo(site)}
            alt={`Brasão de ${parishName(site)}`}
            className="w-full max-w-[260px]"
          />
          <h1 className="font-headline mt-6 text-2xl leading-tight font-semibold">
            {parishName(site)}
          </h1>
          <p className="mt-3 text-sm leading-relaxed text-white/70">
            Portal de gestão de conteúdos, mensagens e pedidos da freguesia.
          </p>
        </div>

        {/* Form panel */}
        <div className="p-8 sm:p-9">
          <p className="text-primary text-xs font-bold tracking-widest uppercase">
            Painel de Administração
          </p>
          <h2 className="font-headline text-on-surface mt-1 text-2xl font-semibold">Entrar</h2>

          <form onSubmit={submit} className="mt-6 flex flex-col gap-4">
            {authError && loginAlert(authError)}

            <div>
              <label
                htmlFor="login-email"
                className="text-on-surface mb-1.5 block text-sm font-medium"
              >
                Email
              </label>
              <input
                id="login-email"
                type="email"
                autoComplete="username"
                value={data.email}
                onChange={(e) => setData('email', e.target.value)}
                placeholder="admin@exemplo.pt"
                className={inputClass}
                required
              />
            </div>

            <div>
              <label
                htmlFor="login-password"
                className="text-on-surface mb-1.5 block text-sm font-medium"
              >
                Palavra-passe
              </label>
              <input
                id="login-password"
                type="password"
                autoComplete="current-password"
                value={data.password}
                onChange={(e) => setData('password', e.target.value)}
                placeholder="••••••••"
                className={inputClass}
                required
              />
            </div>

            <button
              type="submit"
              disabled={processing}
              className="bg-primary hover:bg-primary-dark mt-2 inline-flex w-full items-center justify-center gap-2 rounded-md py-3 font-semibold text-white transition-colors duration-200 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {processing && (
                <span
                  className="h-4 w-4 animate-spin rounded-full border-2 border-white/45 border-t-white"
                  aria-hidden="true"
                />
              )}
              {processing ? 'A entrar…' : 'Entrar'}
            </button>
          </form>

          <Link
            href="/"
            className="text-on-surface-light hover:text-primary mt-6 inline-flex items-center gap-1.5 text-sm transition-colors"
          >
            <Icon name="arrow_back" size="sm" />
            Voltar ao site público
          </Link>
        </div>
      </div>
    </main>
  );
}
